import FloatingYoutube from "@/components/FloatingYoutube";
import Footer from "@/components/nav/Footer";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { FullScreenLoader } from "@/components/ui/LoadingIndicator";
import PlanCard from "@/components/ui/plan/PlanCard";
import PromoPlanData from "@/data/promoPlans.json";
import { useCheckoutControllerGetSubscriptionCheckoutSession } from "@/lib/lutely-api/gen/checkout/checkout";
import {
  clearPendingSubscription,
  getPendingSubscription,
} from "@/lib/storage-utils";
import { Button, Flex, Heading, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

const MAX_AGE_MS = 3 * 60 * 1000; // 3 minutes

export const PromoSubscriptionsPage = () => {
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const { data: user, subscription, isLoading } = useLoggedInUser();
  const { mutateAsync: getCheckoutSession, isIdle } =
    useCheckoutControllerGetSubscriptionCheckoutSession();

  useEffect(() => {
    if (isLoading || !user || !isIdle) return;

    const raw = getPendingSubscription();
    clearPendingSubscription();
    if (!raw || subscription) return;

    let pending: {
      subscriptionIdentifier:
        | "CreatorMonthly"
        | "CreatorAnnual"
        | "LabelMonthly"
        | "LabelAnnual";
      timestamp: number;
    };

    try {
      pending = JSON.parse(raw);
    } catch {
      return;
    }

    if (
      typeof pending.timestamp !== "number" ||
      Date.now() - pending.timestamp > MAX_AGE_MS ||
      !pending.subscriptionIdentifier
    ) {
      return;
    }

    setIsStripeLoading(true);
    (async () => {
      try {
        const result = await getCheckoutSession({
          subscriptionIdentifier: pending.subscriptionIdentifier,
          data: { discounts: [{ promotion_code: "LIFETIME50" }] },
        });

        window.location.href = result.url;
      } catch (err) {
        console.error("Failed to resume subscription checkout", err);
      } finally {
        setIsStripeLoading(false);
      }
    })();
  }, [user, isLoading, getCheckoutSession, subscription, isIdle]);

  if (isLoading || isStripeLoading || isCheckoutLoading) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <ContainerWrapper>
        <Flex
          direction="column"
          className="py-[60px] pt-[36px] items-center justify-center"
        >
          {/* <span className="text-[22px] lg:text-[28px] font-bold text-blue-900 mb-2">Welcome back!</span> */}
          <Heading className="text-[28px] sm:text-[36px] lg:text-[48px] font-bold text-blue-900 text-center leading-tight px-4">
            <span className="block sm:inline">Lock in your</span>{" "}
            <span className="text-[#532f9b] font-['Figtree'] text-[32px] sm:text-[36px] lg:text-[52px] block sm:inline">
              LIFETIME 50% OFF
            </span>
          </Heading>

          <Flex direction="column" className="pt-[36px] w-full items-center">
            <Flex
              justify="center"
              align="center"
              className="gap-[12px] pt-[32px] max-w-[1200px] w-full flex-col lg:flex-row lg:items-stretch"
            >
              {PromoPlanData.map((plan) => (
                <PlanCard
                  key={plan.title}
                  period="monthly"
                  planData={plan}
                  isPromoExperience
                  onCheckoutStart={() => setIsCheckoutLoading(true)}
                  onCheckoutError={() => setIsCheckoutLoading(false)}
                />
              ))}
            </Flex>

            <Flex className="mt-[32px] p-[16px] pl-[48px] pr-[48px] bg-white items-center">
              <Text className="text-[14px]">
                For <b>Enterprise</b> plans please{" "}
                <Button
                  asChild
                  variant="ghost"
                  size="2"
                  className="pl-[7px] text-[14px] h-[30px] text-brand-primary"
                >
                  <a href="mailto:support@lutely.com">Contact Us</a>
                </Button>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </ContainerWrapper>
      <Footer />
      <FloatingYoutube videoId="3HOBbrH5HCs" />
    </>
  );
};
